import React, {
  useMemo,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react'
import { HelmetProvider } from 'react-helmet-async'

import { useQuery } from '@tanstack/react-query'

import { getAccountId, getEmail, isPublisher } from '~/helpers'
import localstorageConstants, {
  getParsedLocalstorage,
  LOCALSTORAGE_CHANGE_EVENT
} from '~/helpers/localstorage-manipulation'
import { listPublisherService } from '~/modules/retailMedia/services/publisher'
import { getAppInfoQueryKey, getAppInfoService } from '~/services/app-info'
import { primary } from '~/styles/themes'

import { getValidColor } from '../../helpers/color'
import appInfoDTO, { getCurrencyUseCents } from './dto'
import HelmetWrapper from './Helmet'

const DEFAULT_FAQ_URL = 'https://developers.vtex.com/docs/guides/vtex-ads'

// Versões anteriores gravavam este padrão em `faqUrl` no localStorage.
const LEGACY_DEFAULT_FAQ_URL = 'https://newtail.freshdesk.com/support/home'

const LINK_CONFIG: LinkConfig[] = ['adTypes', 'sellerId', 'brands']

const buildCurrencyOnlyAppInfo = (
  currencyCode: string,
  accountId: string
): Partial<AppInfoDTOData> => ({
  accountId,
  currencyCode,
  currencyUseCents: getCurrencyUseCents(currencyCode),
  isCurrencyFallback: true
})

/**
 * O fallback é da conta que o buscou. Trocar de conta sem passar pelo logout
 * (`changeAccount` recarrega a página) deixaria a moeda anterior no
 * localStorage, que é lido direto pelos formatadores.
 */
const dropAppInfoFromOtherAccount = (accountId?: string) => {
  const stored = getParsedLocalstorage(localstorageConstants.APP_INFO)

  if (!stored?.isCurrencyFallback || stored.accountId === accountId) {
    return stored
  }

  localStorage.removeItem(localstorageConstants.APP_INFO)

  return null
}

const AppInfoContext = createContext<AppInfoProviderData>(
  {} as AppInfoProviderData
)

const AppInfoProvider = ({ children }: React.PropsWithChildren) => {
  const [appInfo, setAppInfo] = useState<Partial<AppInfoDTOData> | null>(() => {
    const data = dropAppInfoFromOtherAccount(getAccountId())

    if (data?.isCurrencyFallback) return data

    if (data?.isWhiteLabel) {
      const colors = {
        header: getValidColor(data.colors.header),
        primary: getValidColor(data.colors.primary),
        secondary: getValidColor(data.colors.secondary),
        dark: getValidColor(data.colors.dark)
      }

      const faqUrl =
        data.faqUrl === LEGACY_DEFAULT_FAQ_URL ? undefined : data.faqUrl

      return { ...data, colors, faqUrl }
    }

    return null
  })

  const hostname = window.location.hostname

  /**
   * Handle request
   */

  const { status, isPending, data } = useQuery({
    queryKey: [getAppInfoQueryKey, hostname],
    queryFn: async () => {
      const res = await getAppInfoService()

      const data = { ...res?.data }

      return res.data ? appInfoDTO(data) : null
    }
  })

  // O login navega com `history.push` (sem reload) e este provider fica acima
  // do `<Provider>` do Redux, então a sessão precisa ser observada aqui.
  const [accountId, setAccountId] = useState(getAccountId)

  useEffect(() => {
    const syncAccountIdOnTokenChange = (event: Event) => {
      const { key } = (event as CustomEvent<{ key: string }>).detail || {}

      if (key === localstorageConstants.TOKEN) setAccountId(getAccountId())
    }

    document.addEventListener(
      LOCALSTORAGE_CHANGE_EVENT,
      syncAccountIdOnTokenChange
    )

    return () =>
      document.removeEventListener(
        LOCALSTORAGE_CHANGE_EVENT,
        syncAccountIdOnTokenChange
      )
  }, [])

  useEffect(() => {
    if (dropAppInfoFromOtherAccount(accountId)) return

    setAppInfo(current => (current?.isCurrencyFallback ? null : current))
  }, [accountId])

  // `/app-info` resolve white-label por hostname, então contas sem domínio
  // próprio não recebem moeda nenhuma e caem no BRL hardcoded dos formatadores.
  const shouldUseFallbackCurrency =
    status === 'success' && data === null && isPublisher() && !!accountId

  const {
    data: fallbackPublisherCurrency,
    isPending: isFallbackCurrencyPending,
    isError: isFallbackCurrencyFailed
  } = useQuery({
    queryKey: ['app-info-fallback-publisher-currency', accountId],
    enabled: shouldUseFallbackCurrency,
    staleTime: Infinity,
    queryFn: async () => {
      const res = await listPublisherService({
        account_id: accountId,
        count: false
      })

      // `account_id` não está no DTO da API e só filtra porque o validator roda
      // com `whitelist: false`. Sem exigir linha única, habilitar o whitelist lá
      // aplicaria a moeda de um publisher qualquer a toda a aplicação.
      const isSingleMatch = res.data?.length === 1

      return isSingleMatch ? res.data[0].currency_code : null
    }
  })

  useEffect(() => {
    if (!!data && status === 'success') {
      setAppInfo(data)

      localStorage.setItem(localstorageConstants.APP_INFO, JSON.stringify(data))
    }

    if (data === null && status === 'success') {
      if (shouldUseFallbackCurrency) {
        const isFallbackCurrencyUnresolved =
          isFallbackCurrencyPending || isFallbackCurrencyFailed

        // Uma busca pendente ou falha não sabe que a conta é BRL: apagar a
        // chave aqui reverteria para R$ uma moeda já resolvida.
        if (isFallbackCurrencyUnresolved) return

        if (fallbackPublisherCurrency) {
          const fallbackAppInfo = buildCurrencyOnlyAppInfo(
            fallbackPublisherCurrency,
            accountId
          )

          setAppInfo(fallbackAppInfo)

          localStorage.setItem(
            localstorageConstants.APP_INFO,
            JSON.stringify(fallbackAppInfo)
          )

          return
        }
      }

      setAppInfo(data)

      localStorage.removeItem(localstorageConstants.APP_INFO)
    }
  }, [
    data,
    status,
    shouldUseFallbackCurrency,
    isFallbackCurrencyPending,
    isFallbackCurrencyFailed,
    fallbackPublisherCurrency,
    accountId
  ])

  const name = useMemo(() => {
    if (!appInfo?.name) {
      if (status === 'error') return ''
      //
      return appInfo?.name
    }

    return appInfo.name
  }, [appInfo, status])

  const logo = useMemo(() => {
    if (!appInfo?.logoWhiteLabel && !appInfo?.logo) {
      if (status === 'error') return ''

      const validLogo = appInfo?.logoWhiteLabel || appInfo?.logo

      return status === 'pending' ? null : validLogo
    }

    return appInfo?.logoWhiteLabel || appInfo?.logo
  }, [appInfo, status])

  const headerLogo = useMemo(() => {
    if (!logo && !appInfo?.headerLogo) {
      if (status === 'error') return ''

      const validLogo = appInfo?.headerLogo || logo

      return status === 'pending' ? null : validLogo
    }

    return appInfo?.headerLogo || logo
  }, [appInfo, logo, status])

  const colors = useMemo(
    () => ({
      header: appInfo?.colors?.header || '#ffffff',
      primary: appInfo?.colors?.primary || '#0366DD',
      secondary: appInfo?.colors?.secondary || '#ffb450',
      dark: appInfo?.colors?.dark || primary.dark
    }),
    [
      appInfo?.colors?.header,
      appInfo?.colors?.primary,
      appInfo?.colors?.secondary,
      appInfo?.colors?.dark
    ]
  )

  const isWhiteLabel = useMemo(
    () => !!appInfo && !appInfo.isCurrencyFallback,
    [appInfo]
  )

  // Tratamento antigo para usarmos moeda COP sem os centavos.
  // Essa aplicação pode causar confusão e deve ser descontinuada.
  const lengthCentsCurrency = useMemo(() => {
    if (appInfo?.currencyCode === 'COP') {
      return 0
    }

    return 2
  }, [appInfo?.currencyCode])

  /**
   * Handle super admin
   */
  const allowSuperAdmin = useMemo(() => {
    const email = getEmail()

    return email?.includes('@newtail') || email?.includes('@vtex')
  }, [])

  const toggleSuperAdmin = useCallback(() => {
    if (!allowSuperAdmin) return

    const storageValue = localStorage.getItem(localstorageConstants.SUPER_ADMIN)
    const newValue = storageValue === 'true' ? 'false' : 'true'

    localStorage.setItem(localstorageConstants.SUPER_ADMIN, newValue)

    setSuperAdmin(newValue === 'true')
  }, [allowSuperAdmin])

  const [superAdmin, setSuperAdmin] = useState<boolean>(() => {
    const storageValue = localStorage.getItem(localstorageConstants.SUPER_ADMIN)

    if (storageValue) {
      const data = JSON.parse(storageValue)

      return data
    }

    return false
  })

  const contextValue = useMemo(
    () => ({
      allowSuperAdmin,
      superAdmin,
      toggleSuperAdmin,
      linkConfig: LINK_CONFIG,
      callbackLogoutUrl: appInfo?.callbackLogoutUrl,
      colors,
      currencyCode: appInfo?.currencyCode,
      country: appInfo?.country || 'BR',
      currencyUseCents:
        appInfo?.currencyUseCents !== undefined
          ? appInfo?.currencyUseCents
          : true,
      hasRecharge: appInfo?.hasRecharge,
      headerLogo,
      headerTheme: appInfo?.headerTheme || 'light',
      isWhiteLabel,
      isNetwork: appInfo?.isNetwork,
      lengthCentsCurrency,
      logo,
      name,
      faqUrl: appInfo?.faqUrl || DEFAULT_FAQ_URL,
      newTicketUrl:
        appInfo?.newTicketURL ||
        'https://newtail.freshdesk.com/support/tickets/new',
      rechargeEmail: appInfo?.rechargeEmail,
      rechargeMinValue: appInfo?.rechargeMinValue,
      timezone: appInfo?.timezone,
      externalSignupUrl: appInfo?.externalSigupUrl,
      paymentProvider: appInfo?.paymentProvider || 'default',
      isPending,
      isError: status === 'error'
    }),
    [
      allowSuperAdmin,
      appInfo,
      colors,
      headerLogo,
      isPending,
      status,
      isWhiteLabel,
      lengthCentsCurrency,
      logo,
      name,
      superAdmin,
      toggleSuperAdmin
    ]
  )

  return (
    <AppInfoContext.Provider value={contextValue}>
      <HelmetProvider>
        <HelmetWrapper
          appInfo={appInfo}
          name={name}
          isPending={isPending}
          status={status}
        />

        {children}
      </HelmetProvider>
    </AppInfoContext.Provider>
  )
}

function useAppInfo(): AppInfoProviderData {
  const context = useContext(AppInfoContext)

  if (!context) {
    throw new Error('useAppInfo must be used within an AppInfoProvider')
  }

  return context
}

export { AppInfoContext, AppInfoProvider, useAppInfo }
